import { 
  collection, 
  doc, 
  getDoc,
  getDocs,
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where,
  orderBy,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

const CATEGORIES_COLLECTION = 'expenseCategories';
const EXPENSES_COLLECTION = 'expenses';

// ============================================
// EXPENSE CATEGORIES
// ============================================

/**
 * Create default system categories
 */
export const createDefaultCategories = async () => {
  const defaultCategories = [
    { name: 'Stock Damage', type: 'loss', affectsInventory: true, isSystem: true, color: '#f44336', icon: 'BrokenImage' },
    { name: 'Stock Theft', type: 'loss', affectsInventory: true, isSystem: true, color: '#e91e63', icon: 'Security' },
    { name: 'Stock Expiry', type: 'loss', affectsInventory: true, isSystem: true, color: '#9c27b0', icon: 'EventBusy' },
    { name: 'Supplier Return', type: 'loss', affectsInventory: true, isSystem: true, color: '#673ab7', icon: 'Undo' },
    { name: 'Sample/Giveaway', type: 'loss', affectsInventory: true, isSystem: true, color: '#3f51b5', icon: 'CardGiftcard' },
    { name: 'Customer Refund', type: 'expense', affectsInventory: false, isSystem: true, color: '#2196f3', icon: 'MoneyOff' },
    { name: 'Asset Write-Off', type: 'loss', affectsInventory: false, isSystem: true, color: '#00bcd4', icon: 'Delete' },
    { name: 'Operational', type: 'expense', affectsInventory: false, isSystem: true, color: '#009688', icon: 'Business' },
    { name: 'Petty Cash', type: 'expense', affectsInventory: false, isSystem: true, color: '#4caf50', icon: 'LocalAtm' },
    { name: 'Marketing', type: 'expense', affectsInventory: false, isSystem: true, color: '#8bc34a', icon: 'Campaign' },
    { name: 'Utilities', type: 'expense', affectsInventory: false, isSystem: true, color: '#cddc39', icon: 'Power' },
    { name: 'Salary', type: 'expense', affectsInventory: false, isSystem: true, color: '#ff9800', icon: 'Payments' },
  ];

  try {
    const promises = defaultCategories.map(category => 
      addDoc(collection(db, CATEGORIES_COLLECTION), {
        ...category,
        isArchived: false,
        createdAt: serverTimestamp()
      })
    );
    await Promise.all(promises);
    return { success: true };
  } catch (error) {
    console.error('Error creating default categories:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create expense category
 */
export const createCategory = async (categoryData) => {
  try {
    const category = {
      name: categoryData.name,
      type: categoryData.type || 'expense',
      affectsInventory: categoryData.affectsInventory || false,
      isSystem: false,
      color: categoryData.color || '#757575',
      icon: categoryData.icon || 'Category',
      isArchived: false,
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, CATEGORIES_COLLECTION), category);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating category:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Subscribe to categories
 */
export const subscribeToCategories = (callback) => {
  try {
    const q = query(
      collection(db, CATEGORIES_COLLECTION),
      orderBy('createdAt', 'asc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const categories = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));
      callback(categories);
    }, (error) => {
      console.error('Error subscribing to categories:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up categories subscription:', error);
    return () => {};
  }
};

/**
 * Update category
 */
export const updateCategory = async (categoryId, data) => {
  try {
    const categoryRef = doc(db, CATEGORIES_COLLECTION, categoryId);
    await updateDoc(categoryRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating category:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Archive category
 */
export const archiveCategory = async (categoryId) => {
  try {
    const categoryRef = doc(db, CATEGORIES_COLLECTION, categoryId);
    await updateDoc(categoryRef, {
      isArchived: true,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error archiving category:', error);
    return { success: false, error: error.message };
  }
};


// ============================================
// EXPENSES
// ============================================

/**
 * Create expense with stock deduction
 */
export const createExpense = async (expenseData, stockItems = []) => {
  try {
    // Calculate totals
    const totalStockLossValue = stockItems.reduce((sum, item) => sum + item.totalLoss, 0);
    const grandTotal = (parseFloat(expenseData.amount) || 0) + totalStockLossValue;

    const expense = {
      title: expenseData.title,
      categoryId: expenseData.categoryId,
      categoryName: expenseData.categoryName,
      categoryType: expenseData.categoryType,
      date: expenseData.date || new Date().toISOString().split('T')[0],
      amount: parseFloat(expenseData.amount) || 0,
      paymentMethod: expenseData.paymentMethod || 'Cash',
      referenceNo: expenseData.referenceNo || '',
      notes: expenseData.notes || '',
      attachmentUrl: expenseData.attachmentUrl || '',
      recordedBy: expenseData.recordedBy || '',
      recordedByName: expenseData.recordedByName || '',
      branchId: expenseData.branchId || '',
      totalStockLossValue,
      grandTotal,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, EXPENSES_COLLECTION), expense);

    // Add stock items to subcollection and deduct from inventory
    if (stockItems.length > 0) {
      const stockPromises = stockItems.map(async (item) => {
        // Add to subcollection
        await addDoc(
          collection(db, EXPENSES_COLLECTION, docRef.id, 'stockItems'),
          {
            inventoryId: item.inventoryId,
            inventoryName: item.inventoryName,
            qtyLost: item.qtyLost,
            costPerUnit: item.costPerUnit,
            totalLoss: item.totalLoss,
            createdAt: serverTimestamp()
          }
        );

        // Deduct from inventory
        const inventoryRef = doc(db, 'inventory', item.inventoryId);
        const inventorySnap = await getDoc(inventoryRef);
        
        if (inventorySnap.exists()) {
          const currentStock = inventorySnap.data().stock || 0;
          const newStock = currentStock - item.qtyLost;
          
          await updateDoc(inventoryRef, {
            stock: newStock,
            updatedAt: serverTimestamp()
          });

          // Log stock movement
          const movementType = expenseData.categoryName.toLowerCase().includes('damage') ? 'damage' :
                              expenseData.categoryName.toLowerCase().includes('theft') ? 'theft' :
                              expenseData.categoryName.toLowerCase().includes('expiry') ? 'expiry' :
                              expenseData.categoryName.toLowerCase().includes('sample') ? 'sample' :
                              expenseData.categoryName.toLowerCase().includes('return') ? 'supplier_return' : 'loss';

          await addDoc(
            collection(db, 'inventory', item.inventoryId, 'stockMovements'),
            {
              type: movementType,
              qty: -item.qtyLost,
              referenceId: docRef.id,
              costAtTime: item.costPerUnit,
              notes: `${expenseData.categoryName}: ${expenseData.title}`,
              createdAt: serverTimestamp()
            }
          );
        }
      });

      await Promise.all(stockPromises);
    }

    return {
      success: true,
      id: docRef.id,
      message: 'Expense recorded successfully'
    };
  } catch (error) {
    console.error('Error creating expense:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Subscribe to expenses
 */
export const subscribeToExpenses = (callback) => {
  try {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      orderBy('date', 'desc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const expenses = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
      }));
      callback(expenses);
    }, (error) => {
      console.error('Error subscribing to expenses:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up expenses subscription:', error);
    return () => {};
  }
};

/**
 * Get expense by ID with stock items
 */
export const getExpenseById = async (expenseId) => {
  try {
    const docRef = doc(db, EXPENSES_COLLECTION, expenseId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return { success: false, error: 'Expense not found' };
    }

    // Get stock items
    const stockItemsSnapshot = await getDocs(
      collection(db, EXPENSES_COLLECTION, expenseId, 'stockItems')
    );
    
    const stockItems = stockItemsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return {
      success: true,
      data: {
        id: docSnap.id,
        ...docSnap.data(),
        stockItems,
        createdAt: docSnap.data().createdAt?.toDate?.() || new Date(),
        updatedAt: docSnap.data().updatedAt?.toDate?.() || new Date()
      }
    };
  } catch (error) {
    console.error('Error getting expense:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update expense
 */
export const updateExpense = async (expenseId, data) => {
  try {
    const expenseRef = doc(db, EXPENSES_COLLECTION, expenseId);
    await updateDoc(expenseRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating expense:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete expense
 */
export const deleteExpense = async (expenseId) => {
  try {
    await deleteDoc(doc(db, EXPENSES_COLLECTION, expenseId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting expense:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get expenses by date range
 */
export const getExpensesByDateRange = async (startDate, endDate) => {
  try {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const expenses = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: expenses };
  } catch (error) {
    console.error('Error getting expenses by date range:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get expenses by category
 */
export const getExpensesByCategory = async (categoryId) => {
  try {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      where('categoryId', '==', categoryId),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const expenses = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: expenses };
  } catch (error) {
    console.error('Error getting expenses by category:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get total loss by type
 */
export const getTotalLossByType = async (type) => {
  try {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      where('categoryType', '==', type)
    );
    
    const snapshot = await getDocs(q);
    const total = snapshot.docs.reduce((sum, doc) => sum + (doc.data().grandTotal || 0), 0);

    return { success: true, total };
  } catch (error) {
    console.error('Error getting total loss by type:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get stock loss report
 */
export const getStockLossReport = async () => {
  try {
    const q = query(
      collection(db, EXPENSES_COLLECTION),
      where('affectsInventory', '==', true),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const expenses = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    const totalStockLoss = expenses.reduce((sum, exp) => sum + (exp.totalStockLossValue || 0), 0);

    return { 
      success: true, 
      data: expenses,
      totalStockLoss
    };
  } catch (error) {
    console.error('Error getting stock loss report:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all expenses
 */
export const getAllExpenses = async () => {
  try {
    const q = query(collection(db, EXPENSES_COLLECTION), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    
    const expenses = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
    }));

    return { success: true, data: expenses };
  } catch (error) {
    console.error('Error getting expenses:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create daily expenses from POSBilling end sales
 * Creates individual expense records for each category entered
 */
export const createDailyExpenses = async (date, dailyExpenses, currentUser) => {
  try {
    const DAILY_EXPENSE_CATEGORIES = {
      'Rent': { categoryName: 'Rent', type: 'expense' },
      'Utilities': { categoryName: 'Utilities', type: 'expense' },
      'Salaries': { categoryName: 'Salary', type: 'expense' },
      'Transport': { categoryName: 'Operational', type: 'expense' },
      'Maintenance': { categoryName: 'Operational', type: 'expense' },
      'Other': { categoryName: 'Operational', type: 'expense' },
    };

    const createdExpenses = [];

    // Create an expense record for each category with a value
    for (const [categoryKey, amount] of Object.entries(dailyExpenses)) {
      const amountValue = parseFloat(amount) || 0;
      if (amountValue <= 0) continue; // Skip empty entries

      const categoryInfo = DAILY_EXPENSE_CATEGORIES[categoryKey];
      if (!categoryInfo) continue;

      // Find or create the category
      const q = query(
        collection(db, CATEGORIES_COLLECTION),
        where('name', '==', categoryInfo.categoryName)
      );
      const snapshot = await getDocs(q);
      let categoryId = null;

      if (!snapshot.empty) {
        categoryId = snapshot.docs[0].id;
      } else {
        // Create category if it doesn't exist
        const newCatRef = await addDoc(collection(db, CATEGORIES_COLLECTION), {
          name: categoryInfo.categoryName,
          type: categoryInfo.type,
          affectsInventory: false,
          isSystem: true,
          color: '#ff9800',
          isArchived: false,
          createdAt: serverTimestamp()
        });
        categoryId = newCatRef.id;
      }

      // Create expense record
      const expense = {
        title: `Daily ${categoryKey}`,
        categoryId,
        categoryName: categoryInfo.categoryName,
        categoryType: categoryInfo.type,
        date: date || new Date().toISOString().split('T')[0],
        amount: amountValue,
        paymentMethod: 'Cash',
        referenceNo: '',
        notes: `End of day expense - ${categoryKey}`,
        attachmentUrl: '',
        recordedBy: currentUser?.uid || '',
        recordedByName: currentUser?.email || '',
        affectsInventory: false,
        linkedBillId: '',
        stockReturnedOnRefund: false,
        totalStockLossValue: 0,
        grandTotal: amountValue,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, EXPENSES_COLLECTION), expense);
      createdExpenses.push({ id: docRef.id, ...expense });
    }

    return {
      success: true,
      count: createdExpenses.length,
      expenses: createdExpenses,
      message: `${createdExpenses.length} daily expenses recorded`
    };
  } catch (error) {
    console.error('Error creating daily expenses:', error);
    return {
      success: false,
      error: error.message
    };
  }
};


